import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TextField, Autocomplete, Button, Snackbar, Alert, Grid, Box, Typography, Paper, List, ListItem, ListItemText, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Delete as DeleteIcon, Person as StarIcon } from '@mui/icons-material';
import axios from 'axios';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

function AddOrganizationUsers({ userId, onLogout }) {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const [orgId, setOrgId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserForAdmin, setSelectedUserForAdmin] = useState(null);

  // Fetch organization ID for the admin user
  useEffect(() => {
    const fetchOrgId = async () => {
      try {
        const response = await axios.get(`${API_URL}/get_org_id_by_user/`, {
          params: { user_id: userId },
        });
        setOrgId(response.data.org_id);
      } catch (err) {
        setError('Failed to fetch organization ID');
      }
    };

    if (userId) {
      fetchOrgId();
    }
  }, [userId]);

  // Fetch organization users (already added to the organization)
  useEffect(() => {
    const fetchOrganizationUsers = async () => {
      if (orgId) {
        try {
          const response = await axios.get(`${API_URL}/get_organization_users/`, {
            params: { org_id: orgId },
          });
          setOrganizationUsers(response.data);
        } catch (err) {
          setError('Failed to fetch organization users');
        }
      }
    };

    fetchOrganizationUsers();
  }, [orgId]);

  // Search users not already added to the organization
  useEffect(() => {
    const fetchUsers = async () => {
      if (orgId && searchQuery.length > 2) {
        try {
          const response = await axios.get(`${API_URL}/search_users/`, {
            params: { org_id: orgId, q: searchQuery },
          });
          setUsers(response.data);
        } catch (err) {
          setError('Error fetching users');
        }
      }
    };

    fetchUsers();
  }, [orgId, searchQuery]);

  const handleAddUsers = async () => {
    const userIds = selectedUsers.map(user => user.id);

    try {
      await axios.post(`${API_URL}/add_users_to_organization/`, {
        org_id: orgId,
        user_ids: userIds,
      });
      setSnackbarMessage('Users added successfully!');
      setOpenSnackbar(true);
      setSelectedUsers([]);  // Clear selection after adding
    } catch (err) {
      setSnackbarMessage('Failed to add users');
      setOpenSnackbar(true);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/remove_user_from_organization/`, {
        org_id: orgId,
        user_id: userId,
      });
      setSnackbarMessage('User removed successfully!');
      setOpenSnackbar(true);
      setOrganizationUsers((prevUsers) => prevUsers.filter(user => user.id !== userId)); // Update the state after removal
    } catch (err) {
      setSnackbarMessage('Failed to remove user');
      setOpenSnackbar(true);
    }
  };

  const handleMakeAdmin = (userId) => {
    setSelectedUserForAdmin(userId);
    setOpenDialog(true);
  };

  const handleConfirmMakeAdmin = async () => {
    try {
      // Making the selected user as the new admin
      await axios.post(`${API_URL}/make_admin/`, {
        org_id: orgId,
        new_admin_id: selectedUserForAdmin,
      });
      setSnackbarMessage('User is now the admin');
      setOpenSnackbar(true);
      setOpenDialog(false);
      setOrganizationUsers((prevUsers) =>
        prevUsers.map(user =>
          user.id === selectedUserForAdmin ? { ...user, isAdmin: true } : { ...user, isAdmin: false }
        )
      );
    } catch (err) {
      setSnackbarMessage('Failed to make user admin');
      setOpenSnackbar(true);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedUserForAdmin(null);
  };

  return (
    <Box sx={{ padding: 3, }}>
      <Header1 userId={userId} onLogout={onLogout} />


      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
          mt: 10,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
          // fontWeight: 'bold',  // Optional: you can make the text bold
        }}
        gutterBottom
      >
        Manage Organization Users
      </Typography>

      <Paper sx={{ padding: 3, borderRadius: 2, boxShadow: 3 }}>
        {/* Search and Autocomplete Combined into One Field */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(option) => `${option.name} ${option.surname}`}
              onChange={(event, newValue) => setSelectedUsers(newValue)}
              value={selectedUsers}
              inputValue={searchQuery}
              onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and Select Users"
                  variant="outlined"
                  fullWidth
                  placeholder="Start typing to search users..."
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableClearable
              loading={searchQuery.length > 2 && !users.length}
            />
          </Grid>

          {/* Button to Add Users to Organization */}
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              onClick={handleAddUsers}
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                padding: '12px 20px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 3,
                borderRadius: 1
              }}
            >
              Add Selected Users
            </Button>
          </Grid>
        </Grid>

        {/* Display Organization Users */}
        <Box sx={{ marginTop: 4, minHeight: '50vh', }}>
          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
            Users in this Organization
          </Typography>
          <List>
            {organizationUsers.map((user) => (
              <ListItem key={user.id}>
                <Link to={`/public-view-user/${user.id}`} style={{ textDecoration: "none" }}>
                  <ListItemText
                    primary={`${user.name} ${user.surname}`}
                    secondary={user.isAdmin ? 'Admin' : ''}
                  /></Link>
                {/* Make Admin Button with Tooltip */}
                <Tooltip title="Make Admin">
                  <IconButton onClick={() => handleMakeAdmin(user.id)} sx={{ color: 'primary.main' }}>
                    <StarIcon />
                  </IconButton>
                </Tooltip>
                {/* Remove User Button with Tooltip */}
                <Tooltip title="Remove User">
                  <IconButton onClick={() => handleRemoveUser(user.id)} sx={{ color: 'error.main' }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Snackbar for Feedback */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={error ? 'error' : 'success'}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dialog for Confirming Make Admin */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
      >
        <DialogTitle>Confirm Admin Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to make this user the new admin? You will no longer be an admin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmMakeAdmin} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Footer2 />
    </Box>
  );
}

export default AddOrganizationUsers;
